import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { HandCoins, ChevronDown, CheckCircle2, AlertCircle, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_deposit', label: 'Bank Deposit' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const fmt = (v) => `LKR ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Admin-only card: record a lump-sum cash / bank-deposit / cheque payment
 * against a resident's whole account. Amount cascades across unpaid invoices
 * oldest-first; overpayment banks as credit balance; shortfall shows remaining
 * arrears. Live-previews the split as the admin types, and hands the resulting
 * submission_id to the caller so a Receipt modal can pop open immediately.
 *
 * Props:
 *   onRecorded(submissionId) – fired after a successful record
 */
export const RecordPaymentCard = ({ onRecorded }) => {
  const [properties, setProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [residents, setResidents] = useState([]);
  const [query, setQuery] = useState('');
  const [openList, setOpenList] = useState(false);
  const [selected, setSelected] = useState(null); // {_id, name, email, unit_number, property_name}
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const previewTimer = useRef();

  // Fetch resident + property lists once.
  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([
          axios.get(`${API}/admin/users`, { withCredentials: true }),
          axios.get(`${API}/properties`, { withCredentials: true }),
        ]);
        const list = (u.data || [])
          .filter((r) => r.role === 'resident')
          .map((r) => ({
            _id: r._id,
            name: r.name,
            email: r.email,
            unit_number: r.unit_number,
            property_name: r.property_name,
            // Union of the resident's property ids — legacy top-level + multi-unit array —
            // so filtering works for residents who own units across several properties.
            property_ids: Array.from(new Set([
              r.property_id,
              ...(r.property_ids || []),
              ...((r.units || []).map((x) => x.property_id)),
            ].filter(Boolean))),
          }));
        setResidents(list);
        const props = (p.data || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setProperties(props);
      } catch (_) {
        // Non-fatal — user can still search once the list eventually loads
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = residents;
    // Narrow to residents in the chosen property first — multi-unit residents
    // appear here as long as one of their units belongs to that property.
    if (propertyFilter) {
      list = list.filter((r) => (r.property_ids || []).includes(propertyFilter));
    }
    if (!q) return list.slice(0, 20);
    return list
      .filter((r) =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.unit_number || '').toLowerCase().includes(q)
      )
      .slice(0, 40);
  }, [residents, query, propertyFilter]);

  // Live cascade preview (debounced) — recomputes whenever resident/amount changes.
  useEffect(() => {
    clearTimeout(previewTimer.current);
    if (!selected) { setPreview(null); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { setPreview(null); return; }
    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/admin/payments/record-cascade/preview`,
          { params: { resident_id: selected._id, amount: amt }, withCredentials: true }
        );
        setPreview(data);
      } catch (_) {
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => clearTimeout(previewTimer.current);
  }, [selected, amount]);

  const chooseResident = (r) => {
    setSelected(r);
    setQuery(`${r.name}${r.unit_number ? ` · ${r.unit_number}` : ''}`);
    setOpenList(false);
  };

  const clearForm = () => {
    setSelected(null); setQuery(''); setAmount(''); setNote(''); setPreview(null); setMethod('cash'); setPropertyFilter('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error('Choose a resident'); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { toast.error('Enter an amount greater than 0'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/payments/record-cascade`,
        { resident_id: selected._id, amount: amt, method, note: note.trim() || null },
        { withCredentials: true }
      );
      const parts = [`Recorded ${fmt(data.amount)} for ${data.resident_name}`];
      if (data.invoices_fully_paid) parts.push(`${data.invoices_fully_paid} invoice${data.invoices_fully_paid === 1 ? '' : 's'} cleared`);
      if (data.overpayment > 0.005) parts.push(`overpayment ${fmt(data.overpayment)} banked as credit`);
      toast.success(parts.join(' · '));
      onRecorded?.(data.submission_id);
      clearForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="record-payment-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-md bg-emerald-100 flex items-center justify-center">
          <HandCoins className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Record a payment</h2>
          <p className="text-xs text-[#64748b]">
            Record cash / bank-deposit / cheque paid at the office. Amount cascades to the oldest
            unpaid invoice first; overpayment becomes credit; shortfall is shown as arrears.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="record-payment-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Property filter — narrows the resident search to residents in
              this property (matches both top-level property_id and multi-unit
              array). Leave blank to search across every property. */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</Label>
            <select
              value={propertyFilter}
              onChange={(e) => {
                setPropertyFilter(e.target.value);
                // Reset selection so admin can't submit a resident that no
                // longer matches the new filter.
                setSelected(null);
                setQuery('');
              }}
              className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
              data-testid="record-payment-property-select"
            >
              <option value="">All properties</option>
              {properties.map((p) => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Searchable resident dropdown */}
          <div className="relative space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
              Resident <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSelected(null); setOpenList(true); }}
                onFocus={() => setOpenList(true)}
                onBlur={() => setTimeout(() => setOpenList(false), 150)}
                placeholder={propertyFilter ? "Search this property's residents…" : "Search name, email or unit…"}
                className="pl-9"
                data-testid="record-payment-resident-input"
                autoComplete="off"
              />
              {openList && filtered.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 bg-white border border-[#e2e8f0] rounded-md shadow-md mt-1 max-h-64 overflow-y-auto" data-testid="resident-suggestions">
                  {filtered.map((r) => (
                    <button
                      type="button"
                      key={r._id}
                      onMouseDown={(e) => e.preventDefault()} // keep focus so onBlur doesn't fire first
                      onClick={() => chooseResident(r)}
                      className="w-full text-left px-3 py-2 hover:bg-[#f8fafc] text-sm border-b border-[#f1f5f9] last:border-b-0"
                      data-testid={`resident-suggestion-${r._id}`}
                    >
                      <div className="font-medium text-[#0f172a]">{r.name}</div>
                      <div className="text-xs text-[#64748b]">
                        {r.unit_number ? `Unit ${r.unit_number}` : 'No unit'}
                        {r.property_name ? ` · ${r.property_name}` : ''}
                        {r.email ? ` · ${r.email}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {openList && filtered.length === 0 && (query || propertyFilter) && (
                <div className="absolute z-20 top-full left-0 right-0 bg-white border border-[#e2e8f0] rounded-md shadow-md mt-1 p-3 text-xs text-[#94a3b8] italic">
                  No residents match{propertyFilter ? ' this property' : ''}{query ? ` "${query}"` : ''}.
                </div>
              )}
            </div>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
              Amount <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">LKR</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g., 5000.00"
                className="pl-14"
                data-testid="record-payment-amount-input"
              />
            </div>
          </div>

          {/* Method */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Method</Label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
              data-testid="record-payment-method-select"
            >
              {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Reference / Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Cheque #4021, deposit slip 88231"
              data-testid="record-payment-note-input"
            />
          </div>
        </div>

        {/* Live preview */}
        {selected && amount && (
          <div className="border border-emerald-100 bg-emerald-50/50 rounded-md p-4" data-testid="record-payment-preview">
            {previewLoading && !preview ? (
              <p className="text-xs text-emerald-700 italic">Calculating cascade…</p>
            ) : preview ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="text-xs text-emerald-800">
                    <b>{preview.resident_name}</b>
                    {preview.current_credit_balance > 0 && (
                      <span className="ml-2 text-emerald-700">
                        · existing credit: {fmt(preview.current_credit_balance)}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-emerald-800">
                    Outstanding before: <b>{fmt(preview.total_due_before)}</b>
                  </div>
                </div>

                {preview.applied_to.length === 0 ? (
                  <div className="text-sm text-emerald-800 bg-white rounded-md p-3 border border-emerald-100">
                    No unpaid invoices — the entire {fmt(preview.amount)} will be banked as credit balance.
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto border border-emerald-100 rounded-md bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-emerald-50/70 sticky top-0">
                        <tr>
                          <th className="text-left p-2 text-[10px] font-semibold uppercase text-emerald-700">Invoice</th>
                          <th className="text-right p-2 text-[10px] font-semibold uppercase text-emerald-700">Due before</th>
                          <th className="text-right p-2 text-[10px] font-semibold uppercase text-emerald-700">Applied</th>
                          <th className="text-right p-2 text-[10px] font-semibold uppercase text-emerald-700">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.applied_to.map((r) => (
                          <tr key={r.invoice_id} className="border-t border-emerald-100">
                            <td className="p-2">
                              <div className="font-medium text-[#0f172a]">
                                {r.invoice_number || r.label}
                              </div>
                              <div className="text-[10px] text-[#64748b]">
                                {r.label}
                                {r.period_month ? ` · ${r.period_month}/${r.period_year}` : ''}
                                {r.description ? ` · ${r.description}` : ''}
                              </div>
                            </td>
                            <td className="p-2 text-right text-[#334155]">{fmt(r.due_before)}</td>
                            <td className={`p-2 text-right font-semibold ${r.amount_applied > 0 ? 'text-emerald-700' : 'text-[#94a3b8]'}`}>
                              {fmt(r.amount_applied)}
                            </td>
                            <td className={`p-2 text-right font-semibold ${r.fully_paid ? 'text-emerald-700' : 'text-red-700'}`}>
                              {r.fully_paid ? (
                                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Paid</span>
                              ) : (
                                fmt(r.balance_after)
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 text-xs">
                  <div className="bg-white rounded-md p-2 border border-emerald-100">
                    <div className="text-[10px] uppercase tracking-widest text-[#64748b]">Applied</div>
                    <div className="text-sm font-semibold text-emerald-700">{fmt(preview.allocated)}</div>
                  </div>
                  <div className={`rounded-md p-2 border ${preview.arrears_after > 0.005 ? 'bg-red-50 border-red-100' : 'bg-white border-emerald-100'}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#64748b]">Arrears after</div>
                    <div className={`text-sm font-semibold ${preview.arrears_after > 0.005 ? 'text-red-700' : 'text-emerald-700'}`}>
                      {fmt(preview.arrears_after)}
                    </div>
                  </div>
                  <div className={`rounded-md p-2 border ${preview.overpayment > 0.005 ? 'bg-amber-50 border-amber-100' : 'bg-white border-emerald-100'}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#64748b]">Overpayment → credit</div>
                    <div className={`text-sm font-semibold ${preview.overpayment > 0.005 ? 'text-amber-800' : 'text-[#94a3b8]'}`}>
                      {fmt(preview.overpayment)}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-xs text-[#94a3b8] italic">Enter a resident and amount to see the cascade.</p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={clearForm}
            disabled={submitting}
            data-testid="record-payment-clear"
          >
            Clear
          </Button>
          <Button
            type="submit"
            disabled={submitting || !selected || !amount || parseFloat(amount) <= 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="record-payment-submit"
          >
            <HandCoins className="mr-2 h-4 w-4" />
            {submitting ? 'Recording…' : 'Confirm & record payment'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default RecordPaymentCard;
